<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import campaignsAPI from 'dashboard/api/campaigns';
import Modal from 'dashboard/components/Modal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const metrics = ref({
  total_contacts: 0,
  sent_count: 0,
  delivered_count: 0,
  read_count: 0,
  failed_count: 0,
});
const conversations = ref([]);

const accountId = computed(() => route.params.accountId);

const fetchDetails = async () => {
  isLoading.value = true;
  try {
    const response = await campaignsAPI.getConversations(props.campaign.id);
    if (response.data) {
      metrics.value = response.data.metrics || metrics.value;
      conversations.value = response.data.conversations || [];
    }
  } catch (error) {
    if (props.campaign?.metrics) {
      metrics.value = props.campaign.metrics;
    }
  } finally {
    isLoading.value = false;
  }
};

const openConversation = conversationId => {
  emit('close');
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversationId,
    },
  });
};

const getStatusBadgeClass = status => {
  switch (status) {
    case 'read':
      return 'bg-n-teal-3 text-n-teal-11 border-n-teal-6';
    case 'delivered':
      return 'bg-n-blue-3 text-n-blue-11 border-n-blue-6';
    case 'failed':
      return 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6';
    default:
      return 'bg-n-alpha-2 text-n-slate-11 border-n-weak';
  }
};

const getStatusLabel = status => {
  switch (status) {
    case 'read':
      return 'Leído';
    case 'delivered':
      return 'Entregado';
    case 'failed':
      return 'Fallido';
    default:
      return 'Enviado';
  }
};

onMounted(() => {
  fetchDetails();
});
</script>

<template>
  <Modal
    show
    size="medium"
    :on-close="() => emit('close')"
    @close="emit('close')"
  >
    <div class="p-6 flex flex-col gap-6 max-h-[85vh] overflow-y-auto">
      <div class="flex items-center justify-between border-b border-n-weak pb-4">
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ campaign.title }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ campaign.inbox?.name }} • {{ t('CAMPAIGN.WHATSAPP.HEADER_TITLE') }}
          </p>
        </div>
        <Button
          variant="faded"
          color="slate"
          icon="i-lucide-x"
          size="sm"
          @click="emit('close')"
        />
      </div>

      <!-- Metrics Cards -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div class="p-3 rounded-xl bg-n-alpha-2 border border-n-weak flex flex-col items-center">
          <span class="text-xs font-medium text-n-slate-11">📤 Enviados</span>
          <span class="text-xl font-bold text-n-slate-12 mt-1">
            {{ metrics.sent_count }}
          </span>
        </div>
        <div class="p-3 rounded-xl bg-n-blue-2 border border-n-blue-6 flex flex-col items-center">
          <span class="text-xs font-medium text-n-blue-11">🚚 Entregados</span>
          <span class="text-xl font-bold text-n-blue-11 mt-1">
            {{ metrics.delivered_count }}
          </span>
        </div>
        <div class="p-3 rounded-xl bg-n-teal-2 border border-n-teal-6 flex flex-col items-center">
          <span class="text-xs font-medium text-n-teal-11">👀 Leídos</span>
          <span class="text-xl font-bold text-n-teal-11 mt-1">
            {{ metrics.read_count }}
          </span>
        </div>
        <div class="p-3 rounded-xl bg-n-ruby-2 border border-n-ruby-6 flex flex-col items-center">
          <span class="text-xs font-medium text-n-ruby-11">❌ Fallidos</span>
          <span class="text-xl font-bold text-n-ruby-11 mt-1">
            {{ metrics.failed_count }}
          </span>
        </div>
      </div>

      <!-- Conversations List -->
      <div class="flex flex-col gap-3">
        <h4 class="text-sm font-semibold text-n-slate-12">
          Contactos y Chats ({{ conversations.length }})
        </h4>

        <div v-if="isLoading" class="flex justify-center py-8">
          <Spinner />
        </div>

        <div
          v-else-if="conversations.length === 0"
          class="text-center py-6 text-sm text-n-slate-11 bg-n-alpha-1 rounded-xl"
        >
          No hay conversaciones registradas para esta campaña.
        </div>

        <div v-else class="flex flex-col gap-2 max-h-[350px] overflow-y-auto pr-1">
          <div
            v-for="conv in conversations"
            :key="conv.id"
            class="flex items-center justify-between p-3 rounded-xl bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak transition-colors"
          >
            <div class="flex flex-col min-w-0 pr-2">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ conv.contact_name }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ conv.phone_number }}
              </span>
              <span v-if="conv.external_error" class="text-xs text-n-ruby-11 mt-0.5 truncate">
                Error: {{ conv.external_error }}
              </span>
            </div>

            <div class="flex items-center gap-3 flex-shrink-0">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-md border"
                :class="getStatusBadgeClass(conv.delivery_status)"
              >
                {{ getStatusLabel(conv.delivery_status) }}
              </span>

              <Button
                variant="outlined"
                color="slate"
                size="sm"
                icon="i-lucide-external-link"
                label="Ver Chat"
                @click="openConversation(conv.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
